import React, {useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {useWeaveflowCurrentRouteContext} from '../../context';
import {ComparisonViewProvider} from '../SavedViews/ComparisonViewContext';
import {
  metricsListToDict,
  useComparisonViewInstances,
} from '../SavedViews/comparisonViewUtil';
import {useURLSearchParamsDict} from '../util';
import {TraceObjSchemaForBaseObjectClass} from '../wfReactInterface/objectClassQuery';
import {CompareEvaluationsPage} from './CompareEvaluationsPage';
import {ScoreFilter} from './ecpState';

type CompareEvaluationsPageLoadViewProps = {
  entity: string;
  project: string;
};

const parseJsonSafe = (str: string | undefined) => {
  if (!str) return undefined;
  try {
    return JSON.parse(str);
  } catch (e) {
    return undefined;
  }
};

const getViewState = (
  baseView: TraceObjSchemaForBaseObjectClass<'ComparisonView'> | null,
  query: Record<string, string>
) => {
  const urlEvalIds = parseJsonSafe(query.evaluationCallIds);
  const urlMetrics = parseJsonSafe(query.metrics);
  const urlScoreFilters = parseJsonSafe(query.scoreFilters);
  const urlTab = query.tab;

  if (baseView) {
    const viewDef = baseView.val.definition;
    const baseMetrics = metricsListToDict(viewDef.selected_metrics);

    return {
      evaluationCallIds: urlEvalIds ?? viewDef.evaluation_call_ids ?? [],
      selectedMetrics: urlMetrics ?? baseMetrics,
      scoreFilters: urlScoreFilters ?? [],
      activeTab: (urlTab ?? 'summary') as 'summary' | 'results',
    };
  } else {
    const metricsDict = urlMetrics;
    return {
      evaluationCallIds: urlEvalIds ?? [],
      selectedMetrics: metricsDict ?? null,
      scoreFilters: urlScoreFilters ?? [],
      activeTab: (urlTab ?? 'summary') as 'summary' | 'results',
    };
  }
};

export const CompareEvaluationsPageLoadView = ({
  entity,
  project,
}: CompareEvaluationsPageLoadViewProps) => {
  const query = useURLSearchParamsDict();
  const history = useHistory();
  const routerContext = useWeaveflowCurrentRouteContext();
  const projectId = `${entity}/${project}`;
  const viewId = query.view ?? null;

  const {views, loading: viewsLoading} = useComparisonViewInstances(projectId);

  const baseView = useMemo(() => {
    if (!viewId || viewsLoading) {
      return null;
    }
    return (
      (views.find(v => v.object_id === viewId) as
        | TraceObjSchemaForBaseObjectClass<'ComparisonView'>
        | undefined) ?? null
    );
  }, [viewId, views, viewsLoading]);

  const {evaluationCallIds, selectedMetrics, scoreFilters, activeTab} =
    useMemo(() => {
      if (viewId && viewsLoading) {
        return {
          evaluationCallIds: [],
          selectedMetrics: null,
          scoreFilters: [],
          activeTab: 'summary' as const,
        };
      }

      return getViewState(baseView, query);
    }, [viewId, viewsLoading, query, baseView]);

  const updateUrl = useCallback(
    (
      newEvaluationCallIds: string[],
      newMetrics: Record<string, boolean> | null,
      newScoreFilters: ScoreFilter[],
      newTab?: 'summary' | 'results'
    ) => {
      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          newEvaluationCallIds,
          newMetrics,
          newScoreFilters,
          newTab,
          viewId
        )
      );
    },
    [history, entity, project, routerContext, viewId]
  );

  const onEvaluationCallIdsUpdate = useCallback(
    (newEvaluationCallIds: string[]) => {
      updateUrl(newEvaluationCallIds, selectedMetrics, scoreFilters, activeTab);
    },
    [updateUrl, selectedMetrics, scoreFilters, activeTab]
  );

  const setSelectedMetrics = useCallback(
    (newModel: Record<string, boolean>) => {
      updateUrl(evaluationCallIds, newModel, scoreFilters, activeTab);
    },
    [updateUrl, evaluationCallIds, scoreFilters, activeTab]
  );

  const setScoreFilters = useCallback(
    (newFilters: ScoreFilter[]) => {
      updateUrl(evaluationCallIds, selectedMetrics, newFilters, activeTab);
    },
    [updateUrl, evaluationCallIds, selectedMetrics, activeTab]
  );

  const onTabChange = useCallback(
    (tab: 'summary' | 'results') => {
      updateUrl(evaluationCallIds, selectedMetrics, scoreFilters, tab);
    },
    [updateUrl, evaluationCallIds, selectedMetrics, scoreFilters]
  );

  return (
    <ComparisonViewProvider
      entity={entity}
      project={project}
      viewId={viewId}
      evaluationCallIds={evaluationCallIds}
      selectedMetrics={selectedMetrics}
      scoreFilters={scoreFilters}
      activeTab={activeTab}>
      <CompareEvaluationsPage
        entity={entity}
        project={project}
        evaluationCallIds={evaluationCallIds}
        onEvaluationCallIdsUpdate={onEvaluationCallIdsUpdate}
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        scoreFilters={scoreFilters}
        setScoreFilters={setScoreFilters}
        initialTabValue={activeTab}
        onTabChange={onTabChange}
      />
    </ComparisonViewProvider>
  );
};
